import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  invoicesAPI,
  customersAPI,
  vehiclesAPI,
  servicesAPI,
  productsAPI,
  settingsAPI,
} from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Search,
  Wrench,
  Package,
  UserPlus,
  Car,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Check,
  Edit,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Miss', 'Ms.', 'Dr.', 'Prof.', 'Rev.'];
const DRAFTS_STORAGE_KEY = 'invoice_create_drafts_v2';
const ACTIVE_DRAFT_ID_KEY = 'invoice_create_active_draft_id_v2';

const EMPTY_CUSTOMER = {
  title: '',
  name: '',
  phone: '',
  email: '',
  address: '',
};

const EMPTY_VEHICLE = {
  vehicle_number: '',
  chassis_number: '',
  meter_reading: '',
  color: '',
  make: '',
  model: '',
  year: '',
  engine_type: '',
  notes: '',
};

const createDraftId = () =>
  `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const formatDraftTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

export default function InvoiceCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedCustomerId = searchParams.get('customer');

  const [customers, setCustomers] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCostPrice, setShowCostPrice] = useState(false);
  const [showAddServices, setShowAddServices] = useState(true);
  const [showAddParts, setShowAddParts] = useState(true);
  const [showServiceLines, setShowServiceLines] = useState(true);
  const [showPartLines, setShowPartLines] = useState(true);

  const [drafts, setDrafts] = useState([]);
  const [activeDraftId, setActiveDraftId] = useState('');
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [hasHydratedDrafts, setHasHydratedDrafts] = useState(false);

  const [customerId, setCustomerId] = useState(preSelectedCustomerId || '');
  const [vehicleId, setVehicleId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [serviceLines, setServiceLines] = useState([]);
  const [partLines, setPartLines] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [notes, setNotes] = useState('');

  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  const [createNewVehicle, setCreateNewVehicle] = useState(false);

  const [newCustomer, setNewCustomer] = useState(EMPTY_CUSTOMER);
  const [newVehicle, setNewVehicle] = useState(EMPTY_VEHICLE);

  const [showVehicleEditModal, setShowVehicleEditModal] = useState(false);
  const [vehicleFormData, setVehicleFormData] = useState({
    customer_id: '',
    ...EMPTY_VEHICLE,
  });
  const [vehicleSaving, setVehicleSaving] = useState(false);

  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');

  const autosaveTimerRef = useRef(null);
  const latestStateRef = useRef({});

  const persistDrafts = (nextDrafts) => {
    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(nextDrafts));
    setDrafts(nextDrafts);
  };

  const clearForm = (useDefaultTax = true) => {
    setCustomerId(preSelectedCustomerId || '');
    setVehicleId('');
    setSelectedCustomer(null);
    setSelectedVehicle(null);
    setVehicles([]);
    setServiceLines([]);
    setPartLines([]);
    setTaxRate(useDefaultTax ? settings.default_tax_rate || 0 : 0);
    setDiscountAmount(0);
    setDiscountType('fixed');
    setPaymentMethod('cash');
    setPaymentStatus('paid');
    setNotes('');
    setCreateNewCustomer(false);
    setCreateNewVehicle(false);
    setNewCustomer(EMPTY_CUSTOMER);
    setNewVehicle(EMPTY_VEHICLE);
    setShowCostPrice(false);
    setShowAddServices(true);
    setShowAddParts(true);
    setShowServiceLines(true);
    setShowPartLines(true);
    setVehicleSearch('');
    setVehicleDropdownOpen(false);
    setServiceSearch('');
    setProductSearch('');
    setDraftLoaded(false);
  };

const applyDraftToForm = useCallback(
  (draft) => {
    setCustomerId(draft.customerId || '');
    setVehicleId(draft.vehicleId || '');
    setServiceLines(draft.serviceLines || []);
    setPartLines(draft.partLines || []);
    setTaxRate(
      typeof draft.taxRate === 'number'
        ? draft.taxRate
        : settings.default_tax_rate || 0,
    );
    setDiscountAmount(draft.discountAmount || 0);
    setDiscountType(draft.discountType || 'fixed');
    setPaymentMethod(draft.paymentMethod || 'cash');
    setPaymentStatus(draft.paymentStatus || 'paid');
    setNotes(draft.notes || '');
    setCreateNewCustomer(!!draft.createNewCustomer);
    setCreateNewVehicle(!!draft.createNewVehicle);
    setNewCustomer(draft.newCustomer || EMPTY_CUSTOMER);
    setNewVehicle(draft.newVehicle || EMPTY_VEHICLE);
    setShowCostPrice(!!draft.showCostPrice);
    setShowAddServices(
      draft.showAddServices !== undefined ? draft.showAddServices : true,
    );
    setShowAddParts(draft.showAddParts !== undefined ? draft.showAddParts : true);
    setShowServiceLines(
      draft.showServiceLines !== undefined ? draft.showServiceLines : true,
    );
    setShowPartLines(
      draft.showPartLines !== undefined ? draft.showPartLines : true,
    );
    setVehicleSearch(draft.vehicleSearch || '');
    setServiceSearch(draft.serviceSearch || '');
    setProductSearch(draft.productSearch || '');
    setDraftLoaded(true);
  },
  [settings.default_tax_rate],
);

  const getSelectedCustomerName = () => {
    if (createNewCustomer && newCustomer.name) return newCustomer.name.trim();
    if (selectedCustomer?.name) return selectedCustomer.name.trim();
    return '';
  };

  const getSelectedVehicleName = () => {
    if ((createNewCustomer || createNewVehicle) && newVehicle.vehicle_number) {
      return newVehicle.vehicle_number.trim().toUpperCase();
    }
    if (selectedVehicle?.vehicle_number) {
      return selectedVehicle.vehicle_number.trim().toUpperCase();
    }
    return '';
  };

  const buildDraftName = (existingDraft = null) => {
    const customerName = getSelectedCustomerName();
    const vehicleName = getSelectedVehicleName();

    if (customerName && vehicleName) {
      return `${customerName} - ${vehicleName}`;
    }

    if (customerName) {
      return `${customerName} - Invoice Draft`;
    }

    if (vehicleName) {
      return `${vehicleName} - Invoice Draft`;
    }

    return existingDraft?.name || `Invoice Draft ${new Date().toLocaleString()}`;
  };

  const hasMeaningfulDraftData = () => {
    return Boolean(
      customerId ||
        vehicleId ||
        serviceLines.length > 0 ||
        partLines.length > 0 ||
        notes?.trim() ||
        newCustomer.name?.trim() ||
        newCustomer.phone?.trim() ||
        newVehicle.vehicle_number?.trim() ||
        vehicleSearch?.trim() ||
        serviceSearch?.trim() ||
        productSearch?.trim() ||
        discountAmount > 0 ||
        createNewCustomer ||
        createNewVehicle,
    );
  };

  const getDraftPayload = (draftIdOverride = null) => {
    const existingDraft = drafts.find(
      (draft) => draft.id === (draftIdOverride || activeDraftId),
    );

    return {
      id: draftIdOverride || activeDraftId || createDraftId(),
      name: buildDraftName(existingDraft),
      customerId,
      vehicleId,
      serviceLines,
      partLines,
      taxRate,
      discountAmount,
      discountType,
      paymentMethod,
      paymentStatus,
      notes,
      createNewCustomer,
      createNewVehicle,
      newCustomer,
      newVehicle,
      showCostPrice,
      showAddServices,
      showAddParts,
      showServiceLines,
      showPartLines,
      vehicleSearch,
      serviceSearch,
      productSearch,
      savedAt: new Date().toISOString(),
    };
  };


const saveDraftSilently = useCallback(
  (showToast = false, draftIdOverride = null) => {
    const state = latestStateRef.current;

    const hasData = Boolean(
      state.customerId ||
        state.vehicleId ||
        state.serviceLines?.length > 0 ||
        state.partLines?.length > 0 ||
        state.notes?.trim() ||
        state.newCustomer?.name?.trim() ||
        state.newCustomer?.phone?.trim() ||
        state.newVehicle?.vehicle_number?.trim() ||
        state.vehicleSearch?.trim() ||
        state.serviceSearch?.trim() ||
        state.productSearch?.trim() ||
        state.discountAmount > 0 ||
        state.createNewCustomer ||
        state.createNewVehicle,
    );

    if (!hasData) return null;

    try {
      const existingDraft = state.drafts.find(
        (draft) => draft.id === (draftIdOverride || state.activeDraftId),
      );

      const customerName = state.createNewCustomer
        ? state.newCustomer?.name?.trim()
        : state.selectedCustomer?.name?.trim();

      const vehicleName =
        state.createNewCustomer || state.createNewVehicle
          ? state.newVehicle?.vehicle_number?.trim()?.toUpperCase()
          : state.selectedVehicle?.vehicle_number?.trim()?.toUpperCase();

      let draftName = existingDraft?.name || `Invoice Draft ${new Date().toLocaleString()}`;

      if (customerName && vehicleName) {
        draftName = `${customerName} - ${vehicleName}`;
      } else if (customerName) {
        draftName = `${customerName} - Invoice Draft`;
      } else if (vehicleName) {
        draftName = `${vehicleName} - Invoice Draft`;
      }

      const payload = {
        id: draftIdOverride || state.activeDraftId || createDraftId(),
        name: draftName,
        customerId: state.customerId,
        vehicleId: state.vehicleId,
        serviceLines: state.serviceLines,
        partLines: state.partLines,
        taxRate: state.taxRate,
        discountAmount: state.discountAmount,
        discountType: state.discountType,
        paymentMethod: state.paymentMethod,
        paymentStatus: state.paymentStatus,
        notes: state.notes,
        createNewCustomer: state.createNewCustomer,
        createNewVehicle: state.createNewVehicle,
        newCustomer: state.newCustomer,
        newVehicle: state.newVehicle,
        showCostPrice: state.showCostPrice,
        showAddServices: state.showAddServices,
        showAddParts: state.showAddParts,
        showServiceLines: state.showServiceLines,
        showPartLines: state.showPartLines,
        vehicleSearch: state.vehicleSearch,
        serviceSearch: state.serviceSearch,
        productSearch: state.productSearch,
        savedAt: new Date().toISOString(),
      };

      const exists = state.drafts.some((draft) => draft.id === payload.id);

      const nextDrafts = exists
        ? state.drafts.map((draft) => (draft.id === payload.id ? payload : draft))
        : [payload, ...state.drafts];

      localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(nextDrafts));
      setDrafts(nextDrafts);
      setActiveDraftId(payload.id);
      localStorage.setItem(ACTIVE_DRAFT_ID_KEY, payload.id);

      if (showToast) {
        toast.success('Draft saved');
      }

      return payload.id;
    } catch (error) {
      console.error('Failed to save draft:', error);
      if (showToast) {
        toast.error('Failed to save draft');
      }
      return null;
    }
  },
  [],
);


  const handleSaveDraft = () => {
    saveDraftSilently(true);
  };

  const handleLoadDraft = (draftId) => {
    const draft = drafts.find((item) => item.id === draftId);

    if (!draft) {
      toast.error('Draft not found');
      return;
    }

    setActiveDraftId(draft.id);
    localStorage.setItem(ACTIVE_DRAFT_ID_KEY, draft.id);
    applyDraftToForm(draft);
    toast.success('Draft loaded');
  };

  const handleNewDraft = () => {
    setActiveDraftId('');
    localStorage.removeItem(ACTIVE_DRAFT_ID_KEY);
    clearForm(true);
    toast.success('Started a new draft');
  };

  const handleDiscardDraft = () => {
    if (!activeDraftId) {
      clearForm(true);
      toast.success('Current unsaved draft cleared');
      return;
    }

    const nextDrafts = drafts.filter((draft) => draft.id !== activeDraftId);
    persistDrafts(nextDrafts);
    localStorage.removeItem(ACTIVE_DRAFT_ID_KEY);

    const nextActive = nextDrafts[0] || null;

    if (nextActive) {
      setActiveDraftId(nextActive.id);
      localStorage.setItem(ACTIVE_DRAFT_ID_KEY, nextActive.id);
      applyDraftToForm(nextActive);
    } else {
      setActiveDraftId('');
      clearForm(true);
    }

    toast.success('Draft discarded');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          customersRes,
          vehiclesRes,
          servicesRes,
          productsRes,
          settingsRes,
        ] = await Promise.all([
          customersAPI.getAll(),
          vehiclesAPI.getAll(),
          servicesAPI.getAll(),
          productsAPI.getAll(),
          settingsAPI.get(),
        ]);

        setCustomers(customersRes.data);
        setAllVehicles(vehiclesRes.data);
        setServices(servicesRes.data);
        setProducts(productsRes.data);
        setSettings(settingsRes.data);
        setTaxRate(settingsRes.data.default_tax_rate || 0);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

useEffect(() => {
  if (loading) return;

  try {
    const savedDraftsRaw = localStorage.getItem(DRAFTS_STORAGE_KEY);
    const parsedDrafts = savedDraftsRaw ? JSON.parse(savedDraftsRaw) : [];
    const safeDrafts = Array.isArray(parsedDrafts) ? parsedDrafts : [];

    setDrafts(safeDrafts);

    const savedActiveDraftId = localStorage.getItem(ACTIVE_DRAFT_ID_KEY);
    const initialDraft =
      safeDrafts.find((draft) => draft.id === savedActiveDraftId) ||
      safeDrafts[0] ||
      null;

    if (initialDraft) {
      setActiveDraftId(initialDraft.id);
      localStorage.setItem(ACTIVE_DRAFT_ID_KEY, initialDraft.id);
      applyDraftToForm(initialDraft);
    }

    setHasHydratedDrafts(true);
  } catch (error) {
    console.error('Failed to load drafts:', error);
    localStorage.removeItem(DRAFTS_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_DRAFT_ID_KEY);
    setDrafts([]);
    setHasHydratedDrafts(true);
    toast.error('Saved drafts were invalid and removed');
  }
}, [loading, applyDraftToForm]);

  useEffect(() => {
    if (!customerId) {
      setSelectedCustomer(null);
      setSelectedVehicle(null);
      setVehicles([]);
      return;
    }

    const customer = customers.find((c) => String(c.id) === String(customerId));
    setSelectedCustomer(customer || null);

    const customerVehicles = allVehicles.filter(
      (v) => String(v.customer_id) === String(customerId),
    );
    setVehicles(customerVehicles);

    if (!vehicleId) {
      setSelectedVehicle(null);
      return;
    }

    const vehicle = allVehicles.find((v) => String(v.id) === String(vehicleId));
    setSelectedVehicle(vehicle || null);
  }, [customerId, vehicleId, customers, allVehicles]);

  useEffect(() => {
    if (createNewCustomer) {
      setCustomerId('');
      setVehicleId('');
      setSelectedCustomer(null);
      setSelectedVehicle(null);
      setCreateNewVehicle(true);
    }
  }, [createNewCustomer]);

  useEffect(() => {
    latestStateRef.current = {
      customerId,
      vehicleId,
      serviceLines,
      partLines,
      taxRate,
      discountAmount,
      discountType,
      paymentMethod,
      paymentStatus,
      notes,
      createNewCustomer,
      createNewVehicle,
      newCustomer,
      newVehicle,
      showCostPrice,
      showAddServices,
      showAddParts,
      showServiceLines,
      showPartLines,
      vehicleSearch,
      serviceSearch,
      productSearch,
      activeDraftId,
      drafts,
      selectedCustomer,
      selectedVehicle,
    };
  }, [
    customerId,
    vehicleId,
    serviceLines,
    partLines,
    taxRate,
    discountAmount,
    discountType,
    paymentMethod,
    paymentStatus,
    notes,
    createNewCustomer,
    createNewVehicle,
    newCustomer,
    newVehicle,
    showCostPrice,
    showAddServices,
    showAddParts,
    showServiceLines,
    showPartLines,
    vehicleSearch,
    serviceSearch,
    productSearch,
    activeDraftId,
    drafts,
    selectedCustomer,
    selectedVehicle,
  ]);

useEffect(() => {
  if (!hasHydratedDrafts || loading) return;

  if (autosaveTimerRef.current) {
    clearTimeout(autosaveTimerRef.current);
  }

  autosaveTimerRef.current = setTimeout(() => {
    saveDraftSilently(false);
  }, 800);

  return () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
  };
}, [hasHydratedDrafts, loading, saveDraftSilently]);

useEffect(() => {
  const handleBeforeUnload = () => {
    saveDraftSilently(false);
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    saveDraftSilently(false);
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [saveDraftSilently]);

  const addServiceLine = (service = null) => {
    setServiceLines([
      ...serviceLines,
      {
        id: Date.now(),
        service_id: service?.id || null,
        description: service?.name || '',
        amount: service?.default_price || 0,
        cost: service?.cost || 0,
      },
    ]);
  };

  const addPartLine = (product = null) => {
    if (product && product.stock <= 0) {
      toast.error(`${product.name} is out of stock`);
      return;
    }

    const unitPrice = parseFloat(product?.unit_price ?? 0) || 0;
    const costPrice = parseFloat(product?.cost_price ?? unitPrice) || 0;
    const sellPrice = parseFloat(product?.sell_price ?? unitPrice) || 0;

    setPartLines([
      ...partLines,
      {
        id: Date.now(),
        product_id: product?.id || null,
        description: product?.name || '',
        qty: '1.000',
        cost_price: costPrice.toFixed(2),
        unit_price: unitPrice.toFixed(2),
        sell_price: sellPrice.toFixed(2),
        discount_percent:
          unitPrice > 0
            ? (((unitPrice - sellPrice) / unitPrice) * 100).toFixed(2)
            : '0.00',
        discount_value: (unitPrice - sellPrice).toFixed(2),
        max_stock: product?.stock || 999,
      },
    ]);
  };

  const updateServiceLine = (id, field, value) => {
    setServiceLines(
      serviceLines.map((line) =>
        line.id === id ? { ...line, [field]: value } : line,
      ),
    );
  };

  const updatePartLine = (id, field, value) => {
    setPartLines(
      partLines.map((line) => {
        if (line.id !== id) return line;

        const updatedLine = { ...line, [field]: value };

        const qty = parseFloat(field === 'qty' ? value : updatedLine.qty) || 0;
        const unitPrice =
          parseFloat(field === 'unit_price' ? value : updatedLine.unit_price) || 0;
        const sellPrice =
          parseFloat(field === 'sell_price' ? value : updatedLine.sell_price) || 0;
        const discountPercent =
          parseFloat(
            field === 'discount_percent'
              ? value
              : updatedLine.discount_percent,
          ) || 0;
        const discountValue =
          parseFloat(
            field === 'discount_value' ? value : updatedLine.discount_value,
          ) || 0;

        if (field === 'unit_price') {
          const recalculatedCostPrice = unitPrice;
          const recalculatedSellPrice = unitPrice * (1 - discountPercent / 100);
          const recalculatedDiscountValue = (unitPrice - recalculatedSellPrice) * qty;

          updatedLine.cost_price = Math.max(0, recalculatedCostPrice).toFixed(2);
          updatedLine.sell_price = Math.max(0, recalculatedSellPrice).toFixed(2);
          updatedLine.discount_value = Math.max(
            0,
            recalculatedDiscountValue,
          ).toFixed(2);
        }

        if (field === 'discount_percent') {
          const recalculatedDiscountValue =
            (unitPrice * qty * discountPercent) / 100;
          const recalculatedSellPrice = unitPrice * (1 - discountPercent / 100);

          updatedLine.discount_value = Math.max(
            0,
            recalculatedDiscountValue,
          ).toFixed(2);
          updatedLine.sell_price = Math.max(0, recalculatedSellPrice).toFixed(2);
        }

        if (field === 'discount_value') {
          const totalUnitPrice = unitPrice * qty;
          const recalculatedDiscountPercent =
            totalUnitPrice > 0 ? (discountValue / totalUnitPrice) * 100 : 0;
          const recalculatedSellPrice =
            qty > 0 ? unitPrice - discountValue / qty : unitPrice;

          updatedLine.discount_percent = Math.max(
            0,
            recalculatedDiscountPercent,
          ).toFixed(2);
          updatedLine.sell_price = Math.max(0, recalculatedSellPrice).toFixed(2);
        }

        if (field === 'sell_price') {
          const discountPerUnit = unitPrice - sellPrice;
          const recalculatedDiscountPercent =
            unitPrice > 0 ? (discountPerUnit / unitPrice) * 100 : 0;
          const recalculatedDiscountValue = discountPerUnit * qty;

          updatedLine.discount_percent = Math.max(
            0,
            recalculatedDiscountPercent,
          ).toFixed(2);
          updatedLine.discount_value = Math.max(
            0,
            recalculatedDiscountValue,
          ).toFixed(2);
        }

        if (field === 'qty') {
          const recalculatedDiscountValue =
            (unitPrice * qty * (parseFloat(updatedLine.discount_percent) || 0)) /
            100;

          updatedLine.discount_value = Math.max(
            0,
            recalculatedDiscountValue,
          ).toFixed(2);
        }

        return updatedLine;
      }),
    );
  };

  const removeServiceLine = (id) => {
    setServiceLines(serviceLines.filter((line) => line.id !== id));
  };

  const removePartLine = (id) => {
    setPartLines(partLines.filter((line) => line.id !== id));
  };

  const handleOpenVehicleEditModal = () => {
    if (!selectedVehicle) {
      toast.error('Please select a vehicle first');
      return;
    }

    setVehicleFormData({
      customer_id: String(selectedVehicle.customer_id || customerId || ''),
      vehicle_number: selectedVehicle.vehicle_number || '',
      chassis_number: selectedVehicle.chassis_number || '',
      meter_reading:
        selectedVehicle.meter_reading !== null &&
        selectedVehicle.meter_reading !== undefined
          ? String(selectedVehicle.meter_reading)
          : '',
      color: selectedVehicle.color || '',
      make: selectedVehicle.make || '',
      model: selectedVehicle.model || '',
      year:
        selectedVehicle.year !== null && selectedVehicle.year !== undefined
          ? String(selectedVehicle.year)
          : '',
      engine_type: selectedVehicle.engine_type || '',
      notes: selectedVehicle.notes || '',
    });
    setShowVehicleEditModal(true);
  };

  const handleUpdateVehicle = async (e) => {
    e.preventDefault();

    if (!selectedVehicle) {
      toast.error('Please select a vehicle first');
      return;
    }

    if (!vehicleFormData.customer_id || !vehicleFormData.vehicle_number) {
      toast.error('Please fill in customer and vehicle number');
      return;
    }

    setVehicleSaving(true);

    try {
      const payload = {
        customer_id: vehicleFormData.customer_id,
        vehicle_number: vehicleFormData.vehicle_number.toUpperCase(),
        chassis_number: vehicleFormData.chassis_number?.toUpperCase() || null,
        meter_reading: vehicleFormData.meter_reading
          ? parseInt(vehicleFormData.meter_reading, 10)
          : null,
        color: vehicleFormData.color || null,
        make: vehicleFormData.make?.toUpperCase() || null,
        model: vehicleFormData.model || null,
        year: vehicleFormData.year ? parseInt(vehicleFormData.year, 10) : null,
        engine_type: vehicleFormData.engine_type || null,
        notes: vehicleFormData.notes || null,
      };

      const response = await vehiclesAPI.update(selectedVehicle.id, payload);
      const updatedVehicle = response.data || { ...selectedVehicle, ...payload };

      setAllVehicles((prev) =>
        prev.map((vehicle) =>
          String(vehicle.id) === String(selectedVehicle.id)
            ? { ...vehicle, ...updatedVehicle }
            : vehicle,
        ),
      );

      setVehicles((prev) =>
        prev.map((vehicle) =>
          String(vehicle.id) === String(selectedVehicle.id)
            ? { ...vehicle, ...updatedVehicle }
            : vehicle,
        ),
      );

      setSelectedVehicle((prev) =>
        prev ? { ...prev, ...updatedVehicle } : updatedVehicle,
      );
      setCustomerId(String(updatedVehicle.customer_id || payload.customer_id));

      const customer = customers.find(
        (c) => String(c.id) === String(updatedVehicle.customer_id || payload.customer_id),
      );
      setSelectedCustomer(customer || null);

      setShowVehicleEditModal(false);
      toast.success('Vehicle updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vehicle');
    } finally {
      setVehicleSaving(false);
    }
  };

  const handleVehicleSelect = (vId) => {
    setVehicleId(vId);
    setVehicleDropdownOpen(false);
    const vehicle = allVehicles.find((v) => String(v.id) === String(vId));

    if (vehicle) {
      setSelectedVehicle(vehicle);
      setCustomerId(vehicle.customer_id);

      const customer = customers.find(
        (c) => String(c.id) === String(vehicle.customer_id),
      );
      setSelectedCustomer(customer);
      setVehicles(
        allVehicles.filter(
          (v) => String(v.customer_id) === String(vehicle.customer_id),
        ),
      );
    }
  };

  const serviceTotal = serviceLines.reduce(
    (sum, line) => sum + parseFloat(line.amount || 0),
    0,
  );

  const serviceCostTotal = serviceLines.reduce(
    (sum, line) => sum + parseFloat(line.cost || 0),
    0,
  );

  const partsTotal = partLines.reduce((sum, line) => {
    const sellPrice = parseFloat(line.sell_price) || 0;
    const qty = parseFloat(line.qty) || 0;
    return sum + sellPrice * qty;
  }, 0);

  const partsCostTotal = partLines.reduce((sum, line) => {
    const costPrice = parseFloat(line.cost_price) || 0;
    const qty = parseFloat(line.qty) || 0;
    return sum + costPrice * qty;
  }, 0);

  const subtotal = serviceTotal + partsTotal;
  const discountValue =
    discountType === 'percentage'
      ? subtotal * (discountAmount / 100)
      : discountAmount;
  const subtotalAfterDiscount = subtotal - discountValue;
  const taxAmount = subtotalAfterDiscount * (taxRate / 100);
  const grandTotal = subtotalAfterDiscount + taxAmount;
  const totalCost = serviceCostTotal + partsCostTotal;
  const totalProfit = grandTotal - totalCost;

  const handleSave = async () => {
    if (createNewCustomer) {
      if (!newCustomer.name || !newCustomer.phone) {
        toast.error('Please fill in customer name and phone');
        return;
      }

      if (!newVehicle.vehicle_number) {
        toast.error('Please fill in vehicle number');
        return;
      }
    } else {
      if (!customerId || !vehicleId) {
        toast.error('Please select vehicle (customer will be auto-selected)');
        return;
      }
    }

    if (serviceLines.length === 0 && partLines.length === 0) {
      toast.error('Please add at least one service or part');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        customer_id: createNewCustomer ? null : customerId,
        vehicle_id: createNewCustomer || createNewVehicle ? null : vehicleId,
        new_customer: createNewCustomer
          ? {
              title: newCustomer.title || null,
              name: newCustomer.name,
              phone: newCustomer.phone,
              email: newCustomer.email || null,
              address: newCustomer.address || null,
            }
          : null,
        new_vehicle:
          createNewCustomer || createNewVehicle
            ? {
                vehicle_number: newVehicle.vehicle_number.toUpperCase(),
                chassis_number: newVehicle.chassis_number?.toUpperCase() || null,
                meter_reading: newVehicle.meter_reading
                  ? parseInt(newVehicle.meter_reading, 10)
                  : null,
                color: newVehicle.color || null,
                make: newVehicle.make?.toUpperCase() || null,
                model: newVehicle.model || null,
                year: newVehicle.year ? parseInt(newVehicle.year, 10) : null,
                engine_type: newVehicle.engine_type || null,
                notes: newVehicle.notes || null,
              }
            : null,
        service_lines: serviceLines.map(({ id, ...line }) => ({
          ...line,
          note: '',
          discount: 0,
        })),
        part_lines: partLines.map(
          ({ id, max_stock, discount_percent, discount_value, ...line }) => ({
            ...line,
            qty: parseFloat(line.qty) || 0,
            unit_price: parseFloat(line.unit_price) || 0,
            sell_price: parseFloat(line.sell_price) || 0,
            cost_price: parseFloat(line.cost_price) || 0,
            discount: parseFloat(discount_value) || 0,
          }),
        ),
        tax_rate: taxRate,
        discount_amount: discountAmount,
        discount_type: discountType,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        notes: notes || null,
      };

      const res = await invoicesAPI.create(payload);

      if (activeDraftId) {
        const nextDrafts = drafts.filter((draft) => draft.id !== activeDraftId);
        persistDrafts(nextDrafts);
        localStorage.removeItem(ACTIVE_DRAFT_ID_KEY);
      }

      toast.success(`Invoice ${res.data.invoice_no} created!`);
      navigate(`/invoices/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const filteredVehicles = useMemo(() => {
    const searchTerm = vehicleSearch.trim().toLowerCase();

    if (!searchTerm) {
      return allVehicles;
    }

    return allVehicles.filter((v) => {
      const searchableText = [
        v.vehicle_number,
        v.customer?.name,
        v.make,
        v.model,
        v.chassis_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(searchTerm);
    });
  }, [allVehicles, vehicleSearch]);

  const filteredServices = useMemo(
    () =>
      services.filter((s) =>
        s.name.toLowerCase().includes(serviceSearch.toLowerCase()),
      ),
    [services, serviceSearch],
  );

  const filteredProducts = useMemo(
    () =>
      products.filter(
        (p) =>
          p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
          (p.sku && p.sku.toLowerCase().includes(productSearch.toLowerCase())),
      ),
    [products, productSearch],
  );

  const activeDraft = drafts.find((draft) => draft.id === activeDraftId) || null;
  const selectedVehicleForDropdown = allVehicles.find(
    (v) => String(v.id) === String(vehicleId),
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="invoice-create-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black font-heading tracking-tight">
              New Invoice
            </h1>
            <p className="text-muted-foreground">
              Create a new invoice for services and parts
            </p>
            {draftLoaded && activeDraft && (
              <p className="text-xs text-amber-600 mt-1">
                Loaded draft: {activeDraft.name}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={handleNewDraft}
            data-testid="new-draft-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Draft
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleDiscardDraft}
            data-testid="discard-draft-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Discard Draft
          </Button>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="font-semibold uppercase tracking-wide"
            data-testid="save-invoice-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Invoice'}
          </Button>
        </div>
      </div>

      <Card data-testid="draft-manager-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Draft Manager
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4 items-start">
            <div className="space-y-2">
              <Label>Saved Drafts</Label>
              <Select
                value={activeDraftId || 'none'}
                onValueChange={(value) => {
                  if (value !== 'none') {
                    handleLoadDraft(value);
                  }
                }}
              >
                <SelectTrigger data-testid="draft-select">
                  <SelectValue placeholder="Select a draft to load" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a draft</SelectItem>
                  {drafts.map((draft) => (
                    <SelectItem key={draft.id} value={draft.id}>
                      {draft.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border p-3 text-sm">
              {drafts.length > 0 ? (
                <div className="space-y-2">
                  {drafts.map((draft) => (
                    <div
                      key={draft.id}
                      className={`flex items-center justify-between gap-3 rounded-md border p-3 ${
                        draft.id === activeDraftId ? 'border-primary bg-muted/40' : ''
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{draft.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Saved: {formatDraftTime(draft.savedAt)}
                        </p>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        variant={draft.id === activeDraftId ? 'secondary' : 'outline'}
                        onClick={() => handleLoadDraft(draft.id)}
                        data-testid={`load-draft-${draft.id}`}
                      >
                        Load
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No drafts available yet. Drafts will be auto-saved while you work.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="customer-vehicle-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading">
              Customer & Vehicle Information
            </CardTitle>
            <Button
              variant={createNewCustomer ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCreateNewCustomer(!createNewCustomer)}
              data-testid="toggle-new-customer-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {createNewCustomer ? 'Select Existing' : 'New Customer'}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {createNewCustomer ? (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  New Customer Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Select
                      value={newCustomer.title || 'none'}
                      onValueChange={(value) =>
                        setNewCustomer({
                          ...newCustomer,
                          title: value === 'none' ? '' : value,
                        })
                      }
                    >
                      <SelectTrigger data-testid="new-customer-title">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {TITLE_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-3 space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={newCustomer.name}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, name: e.target.value })
                      }
                      placeholder="Customer name"
                      data-testid="new-customer-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={newCustomer.phone}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, phone: e.target.value })
                      }
                      placeholder="Phone number"
                      data-testid="new-customer-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, email: e.target.value })
                      }
                      placeholder="Email address"
                      data-testid="new-customer-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={newCustomer.address}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, address: e.target.value })
                    }
                    placeholder="Full address"
                    data-testid="new-customer-address"
                  />
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  New Vehicle Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Vehicle Number *</Label>
                    <Input
                      value={newVehicle.vehicle_number}
                      onChange={(e) =>
                        setNewVehicle({
                          ...newVehicle,
                          vehicle_number: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="ABC-1234"
                      data-testid="new-vehicle-number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Make</Label>
                    <Input
                      value={newVehicle.make}
                      onChange={(e) =>
                        setNewVehicle({
                          ...newVehicle,
                          make: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="TOYOTA"
                      data-testid="new-vehicle-make"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Model</Label>
                    <Input
                      value={newVehicle.model}
                      onChange={(e) =>
                        setNewVehicle({ ...newVehicle, model: e.target.value })
                      }
                      placeholder="Corolla"
                      data-testid="new-vehicle-model"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Input
                      type="number"
                      value={newVehicle.year}
                      onChange={(e) =>
                        setNewVehicle({ ...newVehicle, year: e.target.value })
                      }
                      placeholder="2020"
                      data-testid="new-vehicle-year"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Input
                      value={newVehicle.color}
                      onChange={(e) =>
                        setNewVehicle({ ...newVehicle, color: e.target.value })
                      }
                      placeholder="White"
                      data-testid="new-vehicle-color"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Meter Reading</Label>
                    <Input
                      type="number"
                      value={newVehicle.meter_reading}
                      onChange={(e) =>
                        setNewVehicle({
                          ...newVehicle,
                          meter_reading: e.target.value,
                        })
                      }
                      placeholder="Km"
                      data-testid="new-vehicle-mileage"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Chassis Number</Label>
                    <Input
                      value={newVehicle.chassis_number}
                      onChange={(e) =>
                        setNewVehicle({
                          ...newVehicle,
                          chassis_number: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="VIN"
                      data-testid="new-vehicle-chassis"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Vehicle</Label>
                <Popover
                  open={vehicleDropdownOpen}
                  onOpenChange={setVehicleDropdownOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={vehicleDropdownOpen}
                      className="w-full justify-between"
                      data-testid="vehicle-select"
                    >
                      {selectedVehicleForDropdown ? (
                        <span className="min-w-0 truncate text-left">
                          <span className="font-mono font-bold">
                            {selectedVehicleForDropdown.vehicle_number}
                          </span>
                          <span className="text-muted-foreground ml-2">
                            - {selectedVehicleForDropdown.customer?.name}{' '}
                            {selectedVehicleForDropdown.make && selectedVehicleForDropdown.model
                              ? `(${selectedVehicleForDropdown.make} ${selectedVehicleForDropdown.model})`
                              : ''}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          Select vehicle by number
                        </span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Type vehicle number, customer, make or model..."
                        value={vehicleSearch}
                        onValueChange={setVehicleSearch}
                        data-testid="vehicle-search-input"
                      />
                      <CommandList>
                        <CommandEmpty>No vehicle found.</CommandEmpty>
                        <CommandGroup>
                          {filteredVehicles.map((v) => (
                            <CommandItem
                              key={v.id}
                              value={String(v.id)}
                              onSelect={() => handleVehicleSelect(v.id)}
                              data-testid={`vehicle-option-${v.id}`}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  String(vehicleId) === String(v.id)
                                    ? 'opacity-100'
                                    : 'opacity-0'
                                }`}
                              />
                              <span className="min-w-0 truncate">
                                <span className="font-mono font-bold">
                                  {v.vehicle_number}
                                </span>
                                <span className="text-muted-foreground ml-2">
                                  - {v.customer?.name}{' '}
                                  {v.make && v.model ? `(${v.make} ${v.model})` : ''}
                                </span>
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {selectedCustomer && selectedVehicle && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-300 mb-2">
                      Customer Details
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="text-muted-foreground">Name:</span>{' '}
                        {selectedCustomer.title} {selectedCustomer.name}
                      </p>
                      <p>
                        <span className="text-muted-foreground">Phone:</span>{' '}
                        {selectedCustomer.phone}
                      </p>
                      {selectedCustomer.email && (
                        <p>
                          <span className="text-muted-foreground">Email:</span>{' '}
                          {selectedCustomer.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h4 className="font-semibold text-sm text-green-800 dark:text-green-300">
                        Vehicle Details
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleOpenVehicleEditModal}
                        data-testid="edit-selected-vehicle-btn"
                        aria-label="Edit vehicle"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="text-muted-foreground">Number:</span>{' '}
                        <span className="font-mono font-bold">
                          {selectedVehicle.vehicle_number}
                        </span>
                      </p>
                      {selectedVehicle.make && (
                        <p>
                          <span className="text-muted-foreground">Make/Model:</span>{' '}
                          {selectedVehicle.make} {selectedVehicle.model}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showVehicleEditModal} onOpenChange={setShowVehicleEditModal}>
        <DialogContent className="max-w-lg" data-testid="vehicle-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Vehicle</DialogTitle>
            <DialogDescription>Update selected vehicle information</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateVehicle} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-vehicle-customer">Customer *</Label>
              <Select
                value={vehicleFormData.customer_id}
                onValueChange={(value) =>
                  setVehicleFormData({ ...vehicleFormData, customer_id: value })
                }
                required
              >
                <SelectTrigger
                  id="edit-vehicle-customer"
                  data-testid="vehicle-customer-select"
                >
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} - {c.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vehicle_number">Vehicle Number *</Label>
                <Input
                  id="vehicle_number"
                  value={vehicleFormData.vehicle_number}
                  onChange={(e) =>
                    setVehicleFormData({
                      ...vehicleFormData,
                      vehicle_number: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="ABC-1234"
                  required
                  data-testid="vehicle-number-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="chassis_number">Chassis Number</Label>
                <Input
                  id="chassis_number"
                  value={vehicleFormData.chassis_number}
                  onChange={(e) =>
                    setVehicleFormData({
                      ...vehicleFormData,
                      chassis_number: e.target.value.toUpperCase(),
                    })
                  }
                  data-testid="vehicle-chassis-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="make">Make</Label>
                <Input
                  id="make"
                  value={vehicleFormData.make}
                  onChange={(e) =>
                    setVehicleFormData({
                      ...vehicleFormData,
                      make: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="TOYOTA"
                  data-testid="vehicle-make-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={vehicleFormData.model}
                  onChange={(e) =>
                    setVehicleFormData({ ...vehicleFormData, model: e.target.value })
                  }
                  placeholder="Camry"
                  data-testid="vehicle-model-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  value={vehicleFormData.year}
                  onChange={(e) =>
                    setVehicleFormData({ ...vehicleFormData, year: e.target.value })
                  }
                  placeholder="2020"
                  data-testid="vehicle-year-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  value={vehicleFormData.color}
                  onChange={(e) =>
                    setVehicleFormData({ ...vehicleFormData, color: e.target.value })
                  }
                  placeholder="Silver"
                  data-testid="vehicle-color-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="meter_reading">Meter Reading (km)</Label>
                <Input
                  id="meter_reading"
                  type="number"
                  value={vehicleFormData.meter_reading}
                  onChange={(e) =>
                    setVehicleFormData({
                      ...vehicleFormData,
                      meter_reading: e.target.value,
                    })
                  }
                  placeholder="50000"
                  data-testid="vehicle-meter-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowVehicleEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={vehicleSaving}
                data-testid="save-vehicle-btn"
              >
                {vehicleSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-6 xl:flex-row">
        {(showAddServices || showAddParts) && (
          <div className="w-full xl:w-[320px] xl:min-w-[320px] xl:max-w-[380px] space-y-6">
            {showAddServices && (
              <Card data-testid="add-services-card">
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => setShowAddServices(!showAddServices)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Wrench className="w-5 h-5" />
                      Add Services
                    </CardTitle>
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <Input
                    placeholder="Search services..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                  />

                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {filteredServices.map((service) => (
                      <Button
                        key={service.id}
                        variant="outline"
                        className="group w-full justify-between h-auto min-h-[44px] py-2 items-start transition-all duration-200 hover:items-start focus-visible:items-start focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        onClick={() => addServiceLine(service)}
                        data-testid={`add-service-${service.id}`}
                        title={service.name}
                      >
                        <span className="flex-1 text-left text-sm leading-5 truncate group-hover:whitespace-normal group-hover:break-words group-hover:overflow-visible group-hover:truncate-none group-focus-visible:whitespace-normal group-focus-visible:break-words group-focus-visible:overflow-visible group-focus-visible:truncate-none">
                          {service.name}
                        </span>

                        <span className="font-mono ml-2 shrink-0 self-center group-hover:self-start group-focus-visible:self-start">
                          {formatCurrency(service.default_price)}
                        </span>
                      </Button>
                    ))}
                  </div>

                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => addServiceLine()}
                    data-testid="add-custom-service-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Service
                  </Button>
                </CardContent>
              </Card>
            )}

            {!showAddServices && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowAddServices(true)}
                data-testid="show-add-services-btn"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Show Add Services
              </Button>
            )}

            {showAddParts && (
              <Card data-testid="add-parts-card">
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => setShowAddParts(!showAddParts)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Add Parts
                    </CardTitle>
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <Input
                    placeholder="Search parts..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                  />

                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {filteredProducts.map((product) => (
                      <Button
                        key={product.id}
                        variant="outline"
                        className={`group w-full justify-between h-auto min-h-[52px] py-2 items-start transition-all duration-200 hover:items-start focus-visible:items-start focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                          product.stock <= 0 ? 'opacity-50' : ''
                        }`}
                        onClick={() => addPartLine(product)}
                        disabled={product.stock <= 0}
                        data-testid={`add-product-${product.id}`}
                        title={product.name}
                      >
                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-sm leading-5 truncate group-hover:whitespace-normal group-hover:break-words group-hover:overflow-visible group-hover:truncate-none group-focus-visible:whitespace-normal group-focus-visible:break-words group-focus-visible:overflow-visible group-focus-visible:truncate-none">
                            {product.name}
                          </div>

                          <div className="text-xs text-muted-foreground mt-1">
                            Stock: {product.stock}
                          </div>
                        </div>

                        <span className="font-mono ml-2 shrink-0 self-center group-hover:self-start group-focus-visible:self-start">
                          {formatCurrency(product.unit_price)}
                        </span>
                      </Button>
                    ))}
                  </div>

                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => addPartLine()}
                    data-testid="add-custom-part-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Part
                  </Button>
                </CardContent>
              </Card>
            )}

            {!showAddParts && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowAddParts(true)}
                data-testid="show-add-parts-btn"
              >
                <Package className="w-4 h-4 mr-2" />
                Show Add Parts
              </Button>
            )}
          </div>
        )}

        <div className="flex-1 min-w-0 space-y-6">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={showAddServices ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowAddServices((prev) => !prev)}
            >
              {showAddServices ? 'Hide Add Services' : 'Show Add Services'}
            </Button>

            <Button
              type="button"
              variant={showAddParts ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowAddParts((prev) => !prev)}
            >
              {showAddParts ? 'Hide Add Parts' : 'Show Add Parts'}
            </Button>
          </div>

          <Card data-testid="service-lines-card">
            <CardHeader
              className="cursor-pointer"
              onClick={() => setShowServiceLines(!showServiceLines)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading">Services</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground font-mono">
                    {formatCurrency(serviceTotal)}
                  </span>
                  {showServiceLines ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardHeader>

            {showServiceLines && (
              <CardContent>
                {serviceLines.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Name</TableHead>
                        <TableHead className="w-28 text-right">Amount</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {serviceLines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>
                            <Input
                              value={line.description}
                              onChange={(e) =>
                                updateServiceLine(
                                  line.id,
                                  'description',
                                  e.target.value,
                                )
                              }
                              placeholder="Service description"
                              className="min-w-[150px]"
                              data-testid={`service-desc-${line.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              value={line.amount}
                              onChange={(e) =>
                                updateServiceLine(line.id, 'amount', e.target.value)
                              }
                              className="w-28 text-right"
                              data-testid={`service-amount-${line.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeServiceLine(line.id)}
                              className="text-red-500"
                              data-testid={`remove-service-${line.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No services added
                  </p>
                )}

                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="text-right">
                    <span className="text-muted-foreground">Services Total:</span>
                    <span className="ml-4 font-mono font-bold">
                      {formatCurrency(serviceTotal)}
                    </span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          <Card data-testid="part-lines-card">
            <CardHeader
              className="cursor-pointer"
              onClick={() => setShowPartLines(!showPartLines)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading">Parts & Products</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground font-mono">
                    {formatCurrency(partsTotal)}
                  </span>
                  {showPartLines ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardHeader>

            {showPartLines && (
              <>
                <CardHeader className="pt-0">
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCostPrice(!showCostPrice);
                      }}
                      data-testid="toggle-cost-price-btn"
                    >
                      {showCostPrice ? (
                        <EyeOff className="w-4 h-4 mr-1" />
                      ) : (
                        <Eye className="w-4 h-4 mr-1" />
                      )}
                      {showCostPrice ? 'Hide Cost' : 'Show Cost'}
                    </Button>
                  </div>
                </CardHeader>

                <CardContent>
                  {partLines.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[180px]">Item Name</TableHead>
                            <TableHead className="w-16 text-center">Qty</TableHead>
                            {showCostPrice && (
                              <TableHead className="w-24 text-right">Cost</TableHead>
                            )}
                            <TableHead className="w-28 text-right">
                              Unit Price
                            </TableHead>
                            <TableHead className="w-28 text-right">
                              Sell Price
                            </TableHead>
                            <TableHead className="w-24 text-right">Disc %</TableHead>
                            <TableHead className="w-28 text-right">Disc Val</TableHead>
                            <TableHead className="w-28 text-right">Total</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {partLines.map((line) => {
                            const lineTotal =
                              (parseFloat(line.sell_price) || 0) *
                              (parseFloat(line.qty) || 0);

                            const isCustomPart = !line.product_id;

                            return (
                              <TableRow key={line.id}>
                                <TableCell>
                                  <Input
                                    value={line.description}
                                    onChange={(e) =>
                                      updatePartLine(
                                        line.id,
                                        'description',
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Part name"
                                    className="min-w-[160px]"
                                    title={line.description}
                                    data-testid={`part-desc-${line.id}`}
                                  />
                                </TableCell>

                                <TableCell>
                                  <Input
                                    type="number"
                                    step="0.001"
                                    min="0.001"
                                    max={line.max_stock}
                                    value={line.qty}
                                    onChange={(e) =>
                                      updatePartLine(line.id, 'qty', e.target.value)
                                    }
                                    className="w-20 text-center"
                                    data-testid={`part-qty-${line.id}`}
                                  />
                                </TableCell>

                                {showCostPrice && (
                                  <TableCell className="text-right">
                                    {isCustomPart ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={line.cost_price}
                                        onChange={(e) =>
                                          updatePartLine(
                                            line.id,
                                            'cost_price',
                                            e.target.value,
                                          )
                                        }
                                        className="w-24 text-right text-xs"
                                        data-testid={`part-cost-price-${line.id}`}
                                      />
                                    ) : (
                                      <div className="w-24 text-right font-mono text-xs text-muted-foreground">
                                        {formatCurrency(line.cost_price)}
                                      </div>
                                    )}
                                  </TableCell>
                                )}

                                <TableCell>
                                  {isCustomPart ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={line.unit_price}
                                      onChange={(e) =>
                                        updatePartLine(
                                          line.id,
                                          'unit_price',
                                          e.target.value,
                                        )
                                      }
                                      className="w-28 text-right text-xs"
                                      data-testid={`part-unit-price-${line.id}`}
                                    />
                                  ) : (
                                    <div className="w-28 text-right font-mono text-xs">
                                      {formatCurrency(line.unit_price)}
                                    </div>
                                  )}
                                </TableCell>

                                <TableCell>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={line.sell_price}
                                    onChange={(e) =>
                                      updatePartLine(
                                        line.id,
                                        'sell_price',
                                        e.target.value,
                                      )
                                    }
                                    className="w-28 text-right text-xs"
                                    data-testid={`part-sell-price-${line.id}`}
                                  />
                                </TableCell>

                                <TableCell>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={line.discount_percent}
                                    onChange={(e) =>
                                      updatePartLine(
                                        line.id,
                                        'discount_percent',
                                        e.target.value,
                                      )
                                    }
                                    className="w-24 text-right text-xs"
                                    data-testid={`part-disc-percent-${line.id}`}
                                  />
                                </TableCell>

                                <TableCell>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={line.discount_value}
                                    onChange={(e) =>
                                      updatePartLine(
                                        line.id,
                                        'discount_value',
                                        e.target.value,
                                      )
                                    }
                                    className="w-28 text-right text-xs"
                                    data-testid={`part-disc-value-${line.id}`}
                                  />
                                </TableCell>

                                <TableCell className="text-right font-mono font-bold">
                                  {formatCurrency(lineTotal)}
                                </TableCell>

                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removePartLine(line.id)}
                                    className="text-red-500 h-8 w-8"
                                    data-testid={`remove-part-${line.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No parts added
                    </p>
                  )}

                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <div className="text-right">
                      <span className="text-muted-foreground">Parts Total:</span>
                      <span className="ml-4 font-mono font-bold">
                        {formatCurrency(partsTotal)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </>
            )}
          </Card>

          <Card data-testid="totals-card">
            <CardHeader>
              <CardTitle className="font-heading">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tax Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={taxRate}
                        onChange={(e) =>
                          setTaxRate(parseFloat(e.target.value) || 0)
                        }
                        data-testid="tax-rate-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Discount Type</Label>
                      <Select value={discountType} onValueChange={setDiscountType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Discount Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={discountAmount}
                      onChange={(e) =>
                        setDiscountAmount(parseFloat(e.target.value) || 0)
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Payment Status</Label>
                    <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Invoice notes"
                      rows={4}
                    />
                  </div>
                </div>

                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Services</span>
                    <span className="font-mono">{formatCurrency(serviceTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Parts</span>
                    <span className="font-mono">{formatCurrency(partsTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="font-mono">
                      {formatCurrency(discountValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="font-mono">{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-3">
                    <span>Grand Total</span>
                    <span className="font-mono">{formatCurrency(grandTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-3">
                    <span className="text-muted-foreground">Total Profit</span>
                    <span className="font-mono">{formatCurrency(totalProfit)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}